/**
 * Recovery from a stale client after a deploy. **Imported for its side effect** — it
 * exports nothing and registers one `window` listener when the module is evaluated.
 *
 * A deploy replaces the content-hashed route chunks. A tab that has been open across one
 * then fails to import a lazy route, because the chunk it remembers is gone. Reload to
 * pick up the new `index.html` — but only once per cooldown, so a genuinely broken deploy
 * surfaces the error instead of looping forever.
 *
 * `lazyRouteComponent` covers the common case itself, but only for errors whose message
 * matches `isModuleNotFoundError`, and only for route chunks. This also catches
 * (a) dependency preload failures, whose "Unable to preload CSS for …" message that check
 * rejects, and (b) dynamic imports outside the router, like exifreader in `lib/photo.ts`.
 * It is keyed on time rather than error message because Safari omits the filename, so the
 * router's key collides across deploys and stops firing after the first failure
 * (TanStack/router#3327). We do not `preventDefault`, so the error propagates and that
 * path stays live.
 *
 * See `docs/TODO-stale-client.md` for what this does not cover.
 */

const PRELOAD_RELOAD_KEY = "plantings:preloadErrorReloadedAt";
const PRELOAD_RELOAD_COOLDOWN_MS = 10_000;

window.addEventListener("vite:preloadError", () => {
  const lastReload = Number(
    sessionStorage.getItem(PRELOAD_RELOAD_KEY) ?? 0
  );
  if (Date.now() - lastReload < PRELOAD_RELOAD_COOLDOWN_MS) {
    return;
  }

  sessionStorage.setItem(PRELOAD_RELOAD_KEY, String(Date.now()));
  window.location.reload();
});
